import { FormikErrors, FormikProps, FormikValues, getIn } from 'formik';
import _ from 'lodash';
import React from 'react';
import { renderItem } from './components';
import { FormActions } from './form-actions';
import { FormSummary } from './form-summary';
import * as S from './form.styles';
import { IDynamicFormItem, IFormStepsGroup } from './form.types';
import { StepperHeader } from './stepper-header';

interface IDynamicFormBase {
    formikProps: FormikProps<any>;
    onCancel?: (isFormDirty: boolean) => void;
    className?: string;
}

interface IDynamicFormWithGroups extends IDynamicFormBase {
    formStepsGroup: IFormStepsGroup[];
}

interface IDynamicFormWithAsyncGroups extends IDynamicFormBase {
    formStepsGroup: (formikProps: FormikProps<any>) => IFormStepsGroup[];
}

type IDynamicFormProps = IDynamicFormWithGroups | IDynamicFormWithAsyncGroups;

interface IDynamicFormState {
    step: number;
    validatedSteps: Set<number>;
    isLoading: boolean;
}

export class DynamicForm extends React.PureComponent<IDynamicFormProps, IDynamicFormState> {
    private get currentStepGroup(): IFormStepsGroup {
        return this.stepsGroup[this.state.step];
    }

    private get stepsGroup(): IFormStepsGroup[] {
        const { formStepsGroup, formikProps } = this.props;

        return Array.isArray(formStepsGroup) ? formStepsGroup : formStepsGroup(formikProps);
    }

    constructor(props: IDynamicFormProps) {
        super(props);

        this.state = {
            step: 0,
            validatedSteps: new Set(),
            isLoading: false,
        };
    }

    public async componentDidMount(): Promise<void> {
        // Run middlewares on the first opened step
        this.setState({
            isLoading: true,
        });
        await this.applyStepMiddleWare(this.stepsGroup, this.state.step);
        this.setState({
            isLoading: false,
        });
    }

    public render(): React.ReactElement {
        const { formikProps, className } = this.props;

        const { handleSubmit, isSubmitting } = formikProps;

        const group = this.stepsGroup;

        const { step, isLoading } = this.state;

        const isLastStep = step === group.length - 1;
        const isFirstStep = step === 0;

        return (
            <S.FormWrapper onSubmit={handleSubmit}>
                {group.length > 1 && (
                    <S.StepperWrapper>
                        <StepperHeader
                            formStepsGroup={group}
                            label={this.currentStepGroup.label}
                            total={group.length}
                            step={step + 1}
                            onSelectStep={this.handleStepSelect}
                        />
                    </S.StepperWrapper>
                )}
                <S.FieldsWrapper className={className}>{this.renderStepper(group)[step]}</S.FieldsWrapper>
                <FormActions
                    isSubmitting={isSubmitting || isLoading}
                    onCancel={this.props.onCancel && this.handleCancel}
                    onNextStep={!isLastStep ? this.handleNextStep : undefined}
                    onPrevStep={!isFirstStep ? this.handlePrevStep : undefined}
                    onSubmit={isLastStep ? this.handleSubmit : undefined}
                />
            </S.FormWrapper>
        );
    }

    private handleStepSelect = (idx: number): void => {
        this.setState({
            step: idx,
        });
    };

    private handleCancel = (): void => {
        this.props.onCancel && this.props.onCancel(this.props.formikProps.dirty);
    };

    private handleSubmit = async (e: React.MouseEvent): Promise<void> => {
        // Prevent submitting the form
        e.preventDefault();

        const isStepValid = await this.validateStep(this.stepsGroup, this.state.step);

        if (isStepValid) {
            this.props.formikProps.submitForm();
        }
    };

    private readonly handleNextStep = async (e?: React.MouseEvent): Promise<void> => {
        const { step } = this.state;

        // Prevent submitting the form
        e && e.preventDefault();

        this.setState({
            isLoading: true,
        });

        try {
            const isStepValid = await this.validateStep(this.stepsGroup, step);
            if (isStepValid) {
                await this.applyStepMiddleWare(this.stepsGroup, step + 1);
                this.setState({
                    step: step + 1,
                });
            }
            this.setState({
                isLoading: false,
            });
        } catch (err) {
            // NB: ensure form unlocks after errors
            this.setState({
                isLoading: false,
            });
        }
    };

    private readonly handlePrevStep = async (e: React.MouseEvent): Promise<void> => {
        const { step } = this.state;

        // Prevent submitting the form
        e.preventDefault();

        this.setState({
            step: step - 1,
        });
    };

    private applyStepMiddleWare = async (formStepsGroup: IFormStepsGroup[], stepIdx: number): Promise<void> => {
        const { formikProps } = this.props;

        const step = formStepsGroup[stepIdx];

        if (step.preMiddleware) {
            await Promise.all(step.preMiddleware.map(middleware => middleware(formikProps, step)));
        }

        return;
    };

    private validateStep = async (formStepsGroup: IFormStepsGroup[], step: number): Promise<boolean> => {
        const { formikProps } = this.props;

        const { validatedSteps } = this.state;

        this.setState({
            validatedSteps: validatedSteps.add(step),
        });

        const validatedErrors = await formikProps.validateForm();

        const computedSchema = formStepsGroup[step].computedSchema;
        const schema = computedSchema
            ? [...formStepsGroup[step].schema, ...computedSchema(formikProps.values)]
            : [...formStepsGroup[step].schema];

        this.markTouchedFields(schema, validatedErrors);

        const isStepValid = schema.every(el => !getIn(validatedErrors, el.name));

        if (!isStepValid) {
            this.focusFirstError(validatedErrors);
        }

        return isStepValid;
    };

    private renderStepper = (formStepsGroup: IFormStepsGroup[]): React.ReactElement[] => {
        const { formikProps } = this.props;

        const onStepSelect = (step: IFormStepsGroup): void => {
            const idx = formStepsGroup.indexOf(step);
            this.handleStepSelect(idx);
        };

        return formStepsGroup.map((step, index) => {
            if (step.isSummaryStep) {
                return (
                    <FormSummary
                        formStepsGroup={formStepsGroup}
                        values={formikProps.values}
                        onStepSelect={onStepSelect}
                    />
                );
            }

            if (step.Component) {
                return step.Component(formikProps);
            }

            const computedSchema = step.computedSchema;
            const schema = computedSchema ? [...step.schema, ...computedSchema(formikProps.values)] : [...step.schema];

            return <section key={index}>{schema.map(renderItem)}</section>;
        });
    };

    private focusFirstError(errors: FormikErrors<FormikValues>): void {
        let temp: any = { ...errors };
        const path = [];
        while (typeof temp !== 'string') {
            /**
             *
             * Array of errors can consist the next format
             * [undefined, {field1: 'val1', field2: 'val2}],
             * It means that first element is valid, so we need to take the first
             * not undefined value
             */
            if (Array.isArray(temp)) {
                const k = temp.find((val, i) => {
                    if (_.isNil(val)) {
                        return false;
                    }

                    path.push(i);
                    return true;
                });

                if (k !== undefined) {
                    temp = k;
                }
                continue;
            }

            const keys = Object.keys(temp);
            const key = keys[0];
            temp = temp[key];
            path.push(key);
        }

        const selector = `[name="${path.join('.')}"]`;
        const errorElement = document.querySelector(selector) as HTMLElement;
        errorElement && errorElement.focus();
    }

    private markTouchedFields(schema: IDynamicFormItem[], errors: FormikErrors<FormikValues>): void {
        const { formikProps } = this.props;

        schema.forEach(item => {
            if (!item?.name) {
                return;
            }

            if (getIn(errors, item.name)) {
                formikProps.setFieldTouched(item.name, true, false);
            }
        });
    }
}
